import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, MessageCircle, X, ArrowRight, Sparkles, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/services/settingsService';
import { useAuth } from '@/services/authService';
import { useSelectedStore, useCategories } from '@/store/dataStore';
import { BIGTIME_WHATSAPP, CATEGORIES } from '@/mock/products';
import { resolveEffectivePrice } from '@/utils/pricing';
import { thumb } from '@/lib/img';

const nameOf = (v) => (typeof v === 'string' ? v : v?.name);

const StockLine = ({ soldOut, material }) => (
  <div className="space-y-2">
    {material && <p className="text-base text-slate-600" data-testid="qv-material">{material}</p>}
    <p data-testid="qv-stock" className={`flex items-center gap-2 text-sm font-semibold ${soldOut ? 'text-red-600' : 'text-teal-700'}`}>
      {soldOut ? <XCircle size={16} /> : <Sparkles size={16} />}
      {soldOut ? 'Agotado' : 'Disponible · En stock'}
    </p>
  </div>
);

const Chips = ({ label, options, value, onChange, testid }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const n = nameOf(o);
        const active = nameOf(value) === n;
        return (
          <button key={n} type="button" onClick={() => onChange(o)} data-testid={`${testid}-${n}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#1B2A4A] text-yellow-300 border-[#1B2A4A]' : 'border-slate-300 text-slate-700 hover:border-[#1B2A4A]'}`}>
            {n}
          </button>
        );
      })}
    </div>
  </div>
);

const QtySelector = ({ qty, setQty, disabled }) => (
  <div className="flex items-center gap-5">
    <span className="text-xs font-bold tracking-[0.25em] text-slate-500">CANTIDAD</span>
    <div className="flex items-center border-2 border-slate-200 rounded-full px-1 bg-white">
      <button type="button" disabled={disabled} onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 disabled:opacity-40 hover:text-[#1B2A4A] transition-colors" data-testid="qv-qty-minus" aria-label="Menos"><Minus size={16} /></button>
      <span className="w-10 text-center font-bold text-lg" data-testid="qv-qty">{qty}</span>
      <button type="button" disabled={disabled} onClick={() => setQty((q) => q + 1)} className="p-2.5 disabled:opacity-40 hover:text-[#1B2A4A] transition-colors" data-testid="qv-qty-plus" aria-label="Más"><Plus size={16} /></button>
    </div>
  </div>
);

const fmtBs = (n) => n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PriceBlock = ({ price, finalPrice, bsRate, discountPct }) => (
  <div className="space-y-1.5" data-testid="qv-price-block">
    <div className="flex items-center gap-3 flex-wrap">
      <span className="font-mono text-4xl font-bold text-[#1B2A4A]" data-testid={discountPct > 0 ? 'qv-price-divisas' : 'qv-price-usd'}>${finalPrice.toFixed(2)}</span>
      {discountPct > 0 && (
        <>
          <span className="font-mono text-lg text-slate-400 line-through" data-testid="qv-price-usd">${price.toFixed(2)}</span>
          <span className="bg-[#F8D13F] text-[#1B2A4A] text-xs font-extrabold px-3 py-1 rounded-full" data-testid="qv-discount">-{discountPct}% DIVISAS</span>
        </>
      )}
    </div>
    {bsRate > 0 && (
      <p className="font-mono text-base text-slate-500" data-testid="qv-price-bs">Bs. {fmtBs(price * bsRate)} · tasa del día</p>
    )}
  </div>
);

export default function ProductQuickView({ product, open, onOpenChange }) {
  const { addItem, subtotal } = useCart();
  const { settings } = useSettings();
  const { user } = useAuth();
  const { store } = useSelectedStore();
  const { categories } = useCategories();
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);

  useEffect(() => { if (open) { setQty(1); setColor(null); setSize(null); } }, [open, product?.id]);
  if (!product) return null;

  const cats = categories?.length ? categories : CATEGORIES;
  const cat = cats.find((c) => c.id === product.category);
  const colors = product.variants?.colors || [];
  const sizes = product.variants?.sizes || [];
  const soldOut = !!product.sold_out;
  const needsChoice = (colors.length > 0 && !color) || (sizes.length > 0 && !size);
  const base = resolveEffectivePrice(product, user, subtotal || 0).price;
  const price = (typeof size === 'object' && size?.price) || color?.price || base;
  const discountPct = Math.max(0, parseFloat(settings?.usd_discount_percent) || 0);
  const image = color?.image || product.image;

  const add = () => {
    addItem({ ...product, price }, qty, { color, size });
    onOpenChange(false);
  };
  const buyWhatsApp = () => {
    const variant = [color && `Color: ${nameOf(color)}`, size && `Talla: ${nameOf(size)}`].filter(Boolean).join(' | ');
    const msg = ['Hola BIGTIME, me interesa este producto:', '', `*${product.name}*`, variant || null,
      `Cantidad: ${qty}`, `Precio: $${(price * qty).toFixed(2)}`, '', '¿Sigue disponible?'].filter((l) => l !== null).join('\n');
    window.open(`https://api.whatsapp.com/send?phone=${store?.whatsapp || BIGTIME_WHATSAPP}&text=${encodeURIComponent(msg)}`, '_blank');
  };

  const finalPrice = discountPct > 0 ? price * (1 - discountPct / 100) : price;
  const badge = product.sold_out ? 'Agotado' : (product.collection || (product.isNew ? 'Nuevo' : ''));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="z-[120] bg-[#1B2A4A]/60 backdrop-blur-sm" className="z-[121] max-w-5xl w-[calc(100%-1.5rem)] p-0 overflow-hidden rounded-[28px] gap-0 max-h-[92vh] overflow-y-auto border-0 [&>button:last-child]:hidden" data-testid="quick-view-modal">
        <DialogClose className="absolute right-4 top-4 md:right-5 md:top-5 z-10 bg-white/90 md:bg-transparent rounded-full text-slate-600 hover:text-[#1B2A4A] hover:rotate-90 transition-transform duration-200 p-1.5" data-testid="qv-close" aria-label="Cerrar">
          <X size={26} strokeWidth={1.5} />
        </DialogClose>
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto md:min-h-[600px] bg-slate-100">
            <img src={thumb(image, 800)} alt={product.name} className="absolute inset-0 w-full h-full object-cover" data-testid="qv-image" />
            {badge && (
              <span data-testid="qv-badge" className={`absolute top-5 left-5 text-xs font-extrabold tracking-[0.15em] uppercase px-4 py-2 rounded-full ${product.sold_out ? 'bg-red-600 text-white' : 'bg-[#F8D13F] text-[#1B2A4A]'}`}>{badge}</span>
            )}
          </div>
          <div className="relative bg-[#FBF8F1] p-6 md:p-10 flex flex-col gap-5 md:justify-center">
            <div className="pr-10">
              {cat && <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#1B2A4A]/70" data-testid="qv-category">{cat.name}</p>}
              <DialogTitle className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A] leading-tight mt-3" data-testid="qv-name">{product.name}</DialogTitle>
              <DialogDescription className="sr-only">Vista rápida de {product.name}</DialogDescription>
            </div>
            <PriceBlock price={price} finalPrice={finalPrice} bsRate={Number(settings?.bs_rate || 0)} discountPct={discountPct} />
            <StockLine soldOut={soldOut} material={product.material} />
            {colors.length > 0 && <Chips label="Color" options={colors} value={color} onChange={setColor} testid="qv-color" />}
            {sizes.length > 0 && <Chips label="Talla" options={sizes} value={size} onChange={setSize} testid="qv-size" />}
            <QtySelector qty={qty} setQty={setQty} disabled={soldOut} />
            <div className="flex flex-col gap-3 pt-1">
              <button type="button" onClick={add} disabled={soldOut || needsChoice} className="w-full bg-[#1B2A4A] hover:bg-[#24375f] text-white font-bold tracking-[0.1em] md:tracking-[0.2em] text-[13px] md:text-sm h-14 px-4 rounded-full uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="qv-add-to-cart">
                {soldOut ? 'Agotado' : needsChoice ? 'Elige una opción' : `Agregar al carrito — $${(finalPrice * qty).toFixed(2)}`}
              </button>
              <button type="button" onClick={buyWhatsApp} disabled={soldOut} className="w-full flex items-center justify-center gap-2.5 border-2 border-[#F8D13F] hover:bg-[#F8D13F] text-[#1B2A4A] font-bold tracking-[0.1em] md:tracking-[0.2em] text-[13px] md:text-sm h-14 px-4 rounded-full uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="qv-buy-whatsapp">
                <MessageCircle size={18} /> Comprar por WhatsApp ahora
              </button>
            </div>
            <Link to={`/producto/${product.id}`} onClick={() => onOpenChange(false)} className="inline-flex items-center gap-1 text-sm font-semibold text-[#1B2A4A]/80 hover:text-[#1B2A4A] hover:underline self-start" data-testid="qv-full-detail">
              Ver detalle completo <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
